import { Link } from "wouter";
import { ChevronRight, Sparkles, Heart, Clock } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import ColourPalettePill from "@/components/common/ColourPalettePill";

export interface ContentItem {
  slug: string;
  title: string;
  type: string;
  category: string;
  description: string;
  bodyTypes: string[];
  occasions: string[];
  colourPalettes: string[];
  shortAnswer: string;
  featuredImage: string;
  readingTime?: number;
  publishedDate?: string;
}

export default function ContentCard({ item }: { item: ContentItem }) {
  const isAskMaria = item.type === "ask-maria";

  return (
    <Link href={`/style-hub/${item.slug}`}>
      <Card className="group h-full bg-card rounded-xl shadow-sm hover:shadow-md transition-all duration-200 cursor-pointer overflow-hidden">
        {/* Card image / placeholder */}
        {item.featuredImage ? (
          <div className="relative h-44 overflow-hidden">
            <img
              src={item.featuredImage}
              alt={item.title}
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
              loading="lazy"
            />
            <div className="absolute top-3 left-3">
              {isAskMaria ? (
                <Badge className="bg-dusty-rose-ink text-white text-xs shadow-sm">Ask Maria</Badge>
              ) : (
                <Badge className="bg-soft-blush-ink text-white text-xs shadow-sm">Guide</Badge>
              )}
            </div>
            {item.readingTime && (
              <div className="absolute top-3 right-3">
                <span className="inline-flex items-center gap-1 bg-white/90 backdrop-blur-sm text-gray-700 text-xs font-medium rounded-full px-2 py-0.5 shadow-sm">
                  <Clock className="w-3 h-3" />
                  {item.readingTime} min
                </span>
              </div>
            )}
          </div>
        ) : (
          <div className={`relative h-44 overflow-hidden ${
            isAskMaria
              ? "bg-gradient-to-br from-dusty-rose/15 via-soft-blush/10 to-cream/20"
              : "bg-gradient-to-br from-cream/50 via-soft-blush/10 to-warm-white"
          }`}>
            <div className="absolute inset-0 opacity-[0.03]" style={{
              backgroundImage: "radial-gradient(circle at 1px 1px, currentColor 1px, transparent 0)",
              backgroundSize: "20px 20px",
            }} />
            <div className="absolute top-3 left-3">
              {isAskMaria ? (
                <Badge className="bg-dusty-rose-ink text-white text-xs shadow-sm">Ask Maria</Badge>
              ) : (
                <Badge className="bg-soft-blush-ink text-white text-xs shadow-sm">Guide</Badge>
              )}
            </div>
            {item.readingTime && (
              <div className="absolute top-3 right-3">
                <span className="inline-flex items-center gap-1 bg-white/90 backdrop-blur-sm text-gray-700 text-xs font-medium rounded-full px-2 py-0.5 shadow-sm">
                  <Clock className="w-3 h-3" />
                  {item.readingTime} min
                </span>
              </div>
            )}
            <div className="absolute bottom-4 right-4 opacity-[0.08]">
              {isAskMaria
                ? <Heart className="w-20 h-20 text-dusty-rose" />
                : <Sparkles className="w-20 h-20 text-soft-blush" />
              }
            </div>
          </div>
        )}

        <CardContent className="p-5 flex flex-col flex-1">
          <div className="flex flex-wrap items-center gap-1.5 mb-2">
            {item.bodyTypes.map((bt) => (
              <Badge key={bt} variant="outline" className="text-xs">
                {bt}
              </Badge>
            ))}
            {item.occasions.map((occ) => (
              <Badge key={occ} variant="outline" className="text-xs">
                {occ}
              </Badge>
            ))}
            {(item.colourPalettes || []).map((cp) => (
              <ColourPalettePill key={cp} palette={cp} />
            ))}
          </div>

          <h3 className="font-serif text-lg font-semibold text-charcoal dark:text-white mb-2 group-hover:text-soft-blush transition-colors line-clamp-2">
            {item.title}
          </h3>

          {isAskMaria && item.shortAnswer ? (
            <p className="text-sm text-gray-600 dark:text-gray-300 mb-4 line-clamp-2 italic">
              "{item.shortAnswer}"
            </p>
          ) : (
            <p className="text-sm text-gray-600 dark:text-gray-300 mb-4 line-clamp-2">
              {item.description}
            </p>
          )}

          <div className="mt-auto flex items-center text-soft-blush text-sm font-medium group-hover:gap-2 transition-all">
            Read more <ChevronRight className="w-4 h-4 ml-1" />
          </div>
        </CardContent>
      </Card>
    </Link>
  );
}
